import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { trackAddToCart, trackRemoveFromCart } from '../lib/gtm';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [serviceType, setServiceType] = useState(null);
  const [storeSubdomain, setStoreSubdomain] = useState(null);
  const [storeCurrency, setStoreCurrency] = useState('EUR');
  const [cartAnimKey, setCartAnimKey] = useState(0);
  const [flyAnim, setFlyAnim] = useState(null);

  // ── Store-scoped cart key ──
  const cartKey = storeSubdomain ? `woyof_cart_${storeSubdomain}` : null;
  const serviceKey = storeSubdomain ? `woyof_svc_${storeSubdomain}` : null;
  const currencyKey = storeSubdomain ? `woyof_cur_${storeSubdomain}` : null;

  // Load cart when store changes
  useEffect(() => {
    if (!cartKey) return;
    const saved = localStorage.getItem(cartKey);
    setCart(saved ? JSON.parse(saved) : []);
  }, [cartKey]);

  // Persist cart to store-scoped key
  useEffect(() => {
    if (!cartKey) return;
    localStorage.setItem(cartKey, JSON.stringify(cart));
  }, [cart, cartKey]);

  // Persist service type per store
  useEffect(() => {
    if (!serviceKey) return;
    if (serviceType) localStorage.setItem(serviceKey, serviceType);
    else localStorage.removeItem(serviceKey);
  }, [serviceType, serviceKey]);

  // When store changes, load that store's service type
  useEffect(() => {
    if (!serviceKey) return;
    const saved = localStorage.getItem(serviceKey);
    setServiceType(saved || null);
  }, [serviceKey]);

  // Load currency when store changes
  useEffect(() => {
    if (!currencyKey) return;
    const saved = localStorage.getItem(currencyKey);
    if (saved) setStoreCurrency(saved);
  }, [currencyKey]);

  // Save currency when it changes
  const updateStoreCurrency = useCallback((cur) => {
    if (!cur) return;
    setStoreCurrency(cur);
    if (currencyKey) localStorage.setItem(currencyKey, cur);
  }, [currencyKey]);

  const addToCart = (product, quantity = 1, selectedOptions = [], notes = '') => {
    const existingItemIndex = cart.findIndex(
      item => item.id === product.id && JSON.stringify(item.selectedOptions) === JSON.stringify(selectedOptions)
    );

    if (existingItemIndex > -1) {
      const newCart = [...cart];
      newCart[existingItemIndex].quantity += quantity;
      if (notes) newCart[existingItemIndex].notes = notes;
      setCart(newCart);
    } else {
      const optionsPrice = selectedOptions.reduce((sum, opt) => sum + opt.price_modifier, 0);
      const itemPrice = product.sale_price || product.base_price;
      
      setCart([...cart, {
        id: product.id,
        name: product.name,
        price: itemPrice,
        base_price: itemPrice,
        options_price: optionsPrice,
        total_price: itemPrice + optionsPrice,
        quantity,
        image: product.images?.[0] || null,
        selectedOptions,
        ...(notes ? { notes } : {})
      }]);
    }
    setCartAnimKey(k => k + 1);
    // Do NOT open cart drawer — non-blocking UX
  };

  // Silent add — for product cards (no toast, no drawer)
  const addItemQuick = useCallback((product, fromRect) => {
    const existingItemIndex = cart.findIndex(item => item.id === product.id && (!item.selectedOptions || item.selectedOptions.length === 0));
    if (existingItemIndex > -1) {
      const newCart = [...cart];
      newCart[existingItemIndex].quantity += 1;
      setCart(newCart);
    } else {
      const itemPrice = product.sale_price || product.base_price;
      setCart(prev => [...prev, {
        id: product.id,
        name: product.name,
        price: itemPrice,
        base_price: itemPrice,
        options_price: 0,
        total_price: itemPrice,
        quantity: 1,
        image: product.images?.[0] || null,
        selectedOptions: []
      }]);
    }
    setCartAnimKey(k => k + 1);
    if (fromRect) {
      setFlyAnim({ id: Date.now(), fromRect, image: product.images?.[0] });
    }
    trackAddToCart(product);
  }, [cart]);

  // Decrement item by product ID (no options)
  const decrementItem = useCallback((productId) => {
    const idx = cart.findIndex(item => item.id === productId && (!item.selectedOptions || item.selectedOptions.length === 0));
    if (idx === -1) return;
    trackRemoveFromCart(cart[idx]);
    if (cart[idx].quantity <= 1) {
      setCart(prev => prev.filter((_, i) => i !== idx));
    } else {
      setCart(prev => {
        const newCart = [...prev];
        newCart[idx] = { ...newCart[idx], quantity: newCart[idx].quantity - 1 };
        return newCart;
      });
    }
    setCartAnimKey(k => k + 1);
  }, [cart]);

  // Get quantity of a product in cart (no options match)
  const getItemQuantity = useCallback((productId) => {
    const item = cart.find(item => item.id === productId && (!item.selectedOptions || item.selectedOptions.length === 0));
    return item ? item.quantity : 0;
  }, [cart]);

  const updateQuantity = (index, quantity) => {
    if (quantity <= 0) {
      removeFromCart(index);
      return;
    }
    const newCart = [...cart];
    newCart[index].quantity = quantity;
    setCart(newCart);
  };

  const removeFromCart = (index) => {
    const newCart = cart.filter((_, i) => i !== index);
    setCart(newCart);
  };

  const getCartTotal = () => {
    return cart.reduce((sum, item) => sum + (item.total_price * item.quantity), 0);
  };

  const getCartCount = () => {
    return cart.reduce((sum, item) => sum + item.quantity, 0);
  };

  const selectServiceType = (type, subdomain) => {
    if (subdomain && subdomain !== storeSubdomain) {
      // Switching store: set subdomain first (will trigger cart reload via useEffect)
      setStoreSubdomain(subdomain);
    }
    setServiceType(type);
  };

  const clearServiceType = () => {
    setServiceType(null);
    if (serviceKey) localStorage.removeItem(serviceKey);
  };

  const clearCart = () => {
    setCart([]);
    if (cartKey) localStorage.removeItem(cartKey);
  };

  const value = {
    cart,
    addToCart,
    addItemQuick,
    decrementItem,
    getItemQuantity,
    updateQuantity,
    removeFromCart,
    clearCart,
    getCartTotal,
    getCartCount,
    isOpen,
    setIsOpen,
    serviceType,
    selectServiceType,
    clearServiceType,
    storeSubdomain,
    setStoreSubdomain,
    storeCurrency,
    updateStoreCurrency,
    cartAnimKey,
    flyAnim,
    setFlyAnim,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
